/**
 * OCRProviderPanel — collapsible "What AI Buck read" debug panel for the
 * OCR review screen. Shows which AI vision model extracted the document,
 * an overall confidence chip, and per-field confidence breakdown so the
 * user knows exactly which fields to double-check.
 *
 * Surfaces signal vs. noise to clients: it visibly demonstrates that
 * CountBucks runs cutting-edge AI vision (Gemini 2.5 Pro / GPT-4o)
 * rather than a black-box OCR engine like the competition.
 */
import { useState } from 'react';
import { Sparkles, ChevronDown, ChevronUp, ShieldCheck, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';

// Provider display metadata — map raw backend identifier to a human label
// + brand color + tooltip. New providers can be added with a single entry.
const PROVIDERS = {
  ai_gemini: {
    label: 'Gemini 2.5 Pro',
    sub: 'Google AI Vision',
    chip: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
  },
  openai_fallback: {
    label: 'GPT-4o Vision',
    sub: 'OpenAI fallback',
    chip: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  },
  amazon_textract: {
    label: 'Amazon Textract',
    sub: 'Legacy provider',
    chip: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  },
};

// Per-doc-type field rosters. The OCR pipeline always extracts the same
// underlying keys (vendor_or_customer / invoice_number / etc.) BUT for
// non-invoice documents most of those keys come back as null, and the
// "Vendor / Invoice number / Subtotal" labels are misleading. We pick
// the right roster + relabel based on the doc_type Gemini classified.
const FIELD_ROSTERS = {
  invoice: {
    vendor_or_customer: 'Customer',
    invoice_number: 'Invoice number',
    invoice_date: 'Invoice date',
    due_date: 'Due date',
    subtotal: 'Subtotal',
    tax_amount: 'Tax',
    total_amount: 'Total',
  },
  bill: {
    vendor_or_customer: 'Vendor',
    invoice_number: 'Bill number',
    invoice_date: 'Bill date',
    due_date: 'Due date',
    subtotal: 'Subtotal',
    tax_amount: 'Tax',
    total_amount: 'Total',
  },
  receipt: {
    vendor_or_customer: 'Merchant',
    invoice_date: 'Date',
    subtotal: 'Subtotal',
    tax_amount: 'Tax',
    total_amount: 'Total',
  },
  tax_return: {
    vendor_or_customer: 'Taxpayer',
    invoice_date: 'Filing date',
  },
  w2_form: {
    vendor_or_customer: 'Employer',
    invoice_date: 'Tax year',
  },
  w9_form: {
    vendor_or_customer: 'Name / Business',
    invoice_number: 'TIN',
  },
  bank_statement: {
    vendor_or_customer: 'Bank',
    invoice_date: 'Statement date',
    total_amount: 'Closing balance',
  },
  contract: {
    vendor_or_customer: 'Counterparty',
    invoice_date: 'Effective date',
  },
  unknown: {
    vendor_or_customer: 'Vendor / Party',
    invoice_number: 'Reference number',
    invoice_date: 'Date',
    total_amount: 'Total',
  },
};
// Default fallback if a doc_type isn't in the roster table.
const DEFAULT_ROSTER = FIELD_ROSTERS.invoice;

function FieldRow({ label, confidence, value }) {
  const pct = Math.round((confidence || 0) * 100);
  const tone = pct >= 85
    ? 'bg-emerald-500'
    : pct >= 70 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div
      className="flex items-center gap-3 text-xs py-1.5"
      data-testid={`ocr-provider-field-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="w-28 flex-shrink-0 text-muted-foreground">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={cn('h-full transition-all', tone)}
          style={{ width: `${Math.max(pct, 4)}%` }}
        />
      </div>
      <span className="w-10 text-right font-mono text-[11px] tabular-nums">{pct}%</span>
      <span className="hidden sm:inline w-24 truncate text-right text-muted-foreground" title={value ?? ''}>
        {value || '—'}
      </span>
    </div>
  );
}

export default function OCRProviderPanel({ job, extraction }) {
  const [open, setOpen] = useState(false);
  if (!job) return null;

  const providerKey = job.ocr_provider || 'ai_gemini';
  const provider = PROVIDERS[providerKey] || {
    label: providerKey,
    sub: 'Unknown provider',
    chip: 'bg-muted text-muted-foreground border-border',
  };
  const avgPct = Math.round((job.avg_confidence || 0) * 100);
  const minPct = Math.round((job.min_confidence || 0) * 100);
  const isFallback = providerKey === 'openai_fallback';

  // Pick the right field roster for the doc_type Gemini detected. Tax
  // returns, W-2s, W-9s etc. don't have "Invoice number" / "Subtotal" /
  // "Tax" — surfacing those labels with null values misleads the user
  // into thinking the OCR missed something it was never asked to find.
  const docType = job.doc_type || 'unknown';
  const roster = FIELD_ROSTERS[docType] || DEFAULT_ROSTER;

  // Build the per-field list lazily — only fields that exist in this
  // doc-type's roster AND that the model actually wrote a value for.
  const fields = Object.entries(roster)
    .map(([key, label]) => {
      const node = extraction?.[key];
      if (!node || typeof node !== 'object') return null;
      return { key, label, confidence: node.confidence || 0, value: node.value };
    })
    .filter(Boolean);

  // Friendly doc-type label for the panel subtitle.
  const docTypeLabel = (
    {
      invoice: 'Invoice',
      bill: 'Bill',
      receipt: 'Receipt',
      tax_return: 'Tax Return',
      w2_form: 'W-2',
      w9_form: 'W-9',
      bank_statement: 'Bank Statement',
      contract: 'Contract',
      unknown: 'Document',
    }[docType] || 'Document'
  );

  return (
    <div
      className="rounded-lg border border-border bg-card overflow-hidden"
      data-testid="ocr-provider-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors"
        aria-expanded={open}
        data-testid="ocr-provider-panel-toggle"
      >
        <div className="flex items-center gap-2 flex-shrink-0">
          <Sparkles className="w-4 h-4 text-emerald-500" />
          <span className="text-xs font-medium">What AI Buck read</span>
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border',
            provider.chip,
          )}
          data-testid="ocr-provider-chip"
        >
          {provider.label}
          {isFallback && <AlertTriangle className="w-2.5 h-2.5" />}
        </span>
        <span className="hidden sm:inline text-[10px] text-muted-foreground font-mono">
          avg {avgPct}% · min {minPct}%
        </span>
        <ShieldCheck className="hidden sm:inline w-3.5 h-3.5 text-muted-foreground ml-auto" />
        {open ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground sm:ml-0 ml-auto" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground sm:ml-0 ml-auto" />
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-border space-y-0.5" data-testid="ocr-provider-panel-body">
          <p className="text-[11px] text-muted-foreground pb-1.5">
            {provider.sub} classified this as <span className="font-medium">{docTypeLabel}</span> and extracted these fields. Lower bars = fields you may want to double-check.
          </p>
          {fields.length === 0 ? (
            <p className="text-xs text-muted-foreground italic py-2">No per-field confidence data available.</p>
          ) : (
            fields.map((f) => (
              <FieldRow key={f.key} label={f.label} confidence={f.confidence} value={f.value} />
            ))
          )}
          {isFallback && (
            <p className="mt-2 text-[10px] text-amber-700 dark:text-amber-400 leading-relaxed">
              Note: Gemini didn&apos;t return a clean response so AI Buck fell back to GPT-4o automatically.
              Numbers should be equally accurate; report any oddities so we can tune the primary model.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
